import {
  getBooleanField,
  getDateField,
  getFloatField,
  getTextField,
  ValidationError,
} from '@lender/utils';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import dayjs from 'dayjs';
import _ from 'lodash';
import { array, object } from 'yup';

import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import { frequencyOptions } from 'constants/options';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { getConditionalObject } from 'utils/data';
import {
  checkIsHomeownersInsuranceImpoundType,
  checkIsImpoundCollected,
  getCalculatedImpoundFields,
  toImpoundCalendarDate,
} from 'utils/impounds';
import { formatNumberToFixed2DecimalsExceptNull } from 'utils/number';
import { getMustBeGreaterThanZeroValidationMessage } from 'utils/validation';

import {
  getIsFollowingCalculatedMonths,
  getMonthsOption,
} from './ImpoundList/ImpoundItem/ImpoundItem.utils';
import { ANNUAL_FREQUENCY_OPTION, MonthsCollectedChangeKind } from './ImpoundSection.constants';

const moreThan0ValidationMessage = getMustBeGreaterThanZeroValidationMessage({
  fieldName: 'Field',
});

const getEarliestDueDate = amountDueDates =>
  _.minBy(_.compact(_.map(amountDueDates, 'dueDate')), dueDate => dayjs(dueDate).valueOf()) ?? null;

export const getHomeownersInsuranceImpoundFields = ({ amountDueDates, annualAmount }) => ({
  frequency: ANNUAL_FREQUENCY_OPTION,
  amountDueDates: [
    { dueDate: getEarliestDueDate(amountDueDates), amountDue: annualAmount ?? null },
  ],
});

export const checkIsImpoundConversionLocked = ({ isPageLocked, lockedFieldsMap }) =>
  !!(
    isPageLocked ||
    lockedFieldsMap.frequency ||
    lockedFieldsMap.dueDate ||
    lockedFieldsMap.amountDue
  );

export const getSchemaImpounds = () => {
  return array().of(
    object().shape({
      impoundTypeId: getTextField(),
      annualAmount: getFloatField().when('pendingChangesActionType', {
        is: PendingChangesActionType.DELETED,
        then: () =>
          getFloatField({ isRequired: false }).moreThan(0, moreThan0ValidationMessage).nullable(),
        otherwise: schema => schema,
      }),
      monthsCollected: getTextField()
        .typeError(ValidationError.RequiredField)
        .when('pendingChangesActionType', {
          is: PendingChangesActionType.DELETED,
          then: () => getTextField({ isRequired: false }).nullable(),
          otherwise: schema => schema,
        }),
      monthlyAmount: getFloatField().when('pendingChangesActionType', {
        is: PendingChangesActionType.DELETED,
        then: () => getFloatField({ isRequired: false }).nullable(),
        otherwise: schema => schema,
      }),
      collectedAmount: getFloatField().when('pendingChangesActionType', {
        is: PendingChangesActionType.DELETED,
        then: () => getFloatField({ isRequired: false }).nullable(),
        otherwise: schema => schema,
      }),
      frequency: getTextField()
        .typeError(ValidationError.RequiredField)
        .when('pendingChangesActionType', {
          is: PendingChangesActionType.DELETED,
          then: () => getTextField({ isRequired: false }).nullable(),
          otherwise: schema => schema,
        }),
      amountDueDates: array().of(
        object().shape({
          dueDate: getDateField().typeError(ValidationError.RequiredField),
          amountDue: getFloatField().moreThan(0, moreThan0ValidationMessage),
        }),
      ),
      paidThroughDate: getDateField({ isRequired: false }).nullable(),
      lastPaymentAmount: getFloatField({ isRequired: false }).nullable(),
      isDeleted: getBooleanField({ isRequired: false }).nullable(),
      pendingChangesActionType: getTextField({ isRequired: false }).nullable(),
    }),
  );
};

const formatRequestDate = value =>
  value ? toImpoundCalendarDate(value).format(COMMON_REQUEST_DATE_FORMAT) : null;

// The form edits calendar dates: an API date is shown and sent as the day it was stored, not the day
// before (see toImpoundCalendarDate).
const toFormDate = value => formatRequestDate(value);

const withFormDates = impound => ({
  ...impound,
  amountDueDates: impound.amountDueDates?.map(amountDueDate => ({
    ...amountDueDate,
    dueDate: toFormDate(amountDueDate.dueDate),
  })),
  paidThroughDate: toFormDate(impound.paidThroughDate),
});

export const getInitialImpounds = ({
  impounds,
  impoundsTypes,
  impoundsTypesOptions,
  homeownersInsuranceImpoundId,
  isImpoundConversionLocked = false,
  taxesInsuranceEscrowed = true,
}) =>
  (impounds || []).map(imp => {
    const { impoundTypeId, frequency, monthsCollected, monthsCollectedOverridden } = imp;
    const { monthlyAmount, collectedAmount } = getCalculatedImpoundFields(imp);
    const formMonthsCollected = taxesInsuranceEscrowed ? monthsCollected : 0;

    const impoundTypeName = impoundsTypes[impoundTypeId]?.name;
    const impoundTypeOption = impoundsTypesOptions.find(o => o.value === impoundTypeId);
    const isHomeownersInsuranceConversionNeeded =
      !isImpoundConversionLocked &&
      getIsNotDeleted(imp) &&
      checkIsHomeownersInsuranceImpoundType(impoundTypeOption, homeownersInsuranceImpoundId);

    const formattedImpound = withFormDates({
      ...imp,
      impoundTypeId: impoundTypeOption,
      frequency: frequencyOptions.find(o => o.value === frequency),
      impoundTypeName,
      monthlyAmount,
      // With escrow off the Months field is pinned to 0 from the start, so opening the form leaves it clean.
      monthsCollected: _.isNil(formMonthsCollected) ? null : getMonthsOption(formMonthsCollected),
      // Impounds saved before the flag existed count as overridden when their months differ from the calculation.
      monthsCollectedOverridden:
        monthsCollectedOverridden ??
        !getIsFollowingCalculatedMonths({
          monthsCollected,
          calculatedMonths: imp.calculatedMonthsCollected,
        }),
      collectedAmount: checkIsImpoundCollected(imp, taxesInsuranceEscrowed) ? collectedAmount : 0,
      ...getConditionalObject(isHomeownersInsuranceConversionNeeded, () =>
        getHomeownersInsuranceImpoundFields(imp),
      ),
    });

    return {
      ...formattedImpound,
      initialFee: formattedImpound,
    };
  });

const getMonthsCollectedChange = ({
  impound,
  isNew,
  isFollowable,
  isChattelLoan,
  isEscrowed,
  isEscrowApplicable,
}) => {
  const isOverridden = impound.monthsCollectedOverridden === true;
  const isInitiallyOverridden = impound.initialFee?.monthsCollectedOverridden === true;
  const isMonthsChanged =
    impound.monthsCollected?.value !== impound.initialFee?.monthsCollected?.value;
  const manualMonthsCollectedChange = {
    kind: MonthsCollectedChangeKind.MANUAL,
    months: impound.monthsCollected?.value ?? null,
  };

  if (isNew) {
    return isFollowable && isEscrowApplicable && !isOverridden ? null : manualMonthsCollectedChange;
  }

  // With escrow off the form forces Months to 0, which must not overwrite the stored months.
  if (!isEscrowed) {
    return null;
  }

  // Escrow is not allowed, so nothing calculates these months: keep what the user picked (Revert
  // included). The form moves Months only on a system-owned chattel row, to follow its due dates.
  if (!isEscrowApplicable) {
    return isMonthsChanged && (!isChattelLoan || isOverridden || isInitiallyOverridden)
      ? manualMonthsCollectedChange
      : null;
  }

  // Only HOI and chattel months are calculated, so the flag decides nothing for other rows.
  if (!isFollowable) {
    return isMonthsChanged ? manualMonthsCollectedChange : null;
  }

  if (isOverridden) {
    return isMonthsChanged || !isInitiallyOverridden ? manualMonthsCollectedChange : null;
  }

  return isInitiallyOverridden || isMonthsChanged
    ? { kind: MonthsCollectedChangeKind.CALCULATED }
    : null;
};

const toRequestImpound = impound => ({
  applicationImpoundId: impound.applicationImpoundId,
  impoundTypeId: impound.impoundTypeId?.value,
  annualAmount: impound.annualAmount,
  monthlyAmount: impound.monthlyAmount,
  collectedAmount: impound.collectedAmount,
  frequency: impound.frequency?.value,
  amountDueDates: _.map(impound.amountDueDates, amountDueDate => ({
    ...amountDueDate,
    dueDate: formatRequestDate(amountDueDate.dueDate),
  })),
  lastPaymentAmount: impound.lastPaymentAmount,
  paidThroughDate: formatRequestDate(impound.paidThroughDate),
  isDeleted: impound.isDeleted,
  pendingChangesActionType: impound.pendingChangesActionType,
});

const toComparableAmount = value =>
  _.isNil(value) || value === '' ? null : formatNumberToFixed2DecimalsExceptNull(Number(value));

// Only the fields the API stores from an UPDATE row: monthly/collected amounts are derived on the
// server, and the months travel separately as monthsCollectedChange.
const getComparableImpound = impound => ({
  impoundTypeId: impound.impoundTypeId ?? null,
  annualAmount: toComparableAmount(impound.annualAmount),
  frequency: impound.frequency ?? null,
  amountDueDates: _.map(impound.amountDueDates, ({ dueDate, amountDue }) => ({
    dueDate: dueDate ?? null,
    amountDue: toComparableAmount(amountDue),
  })),
  paidThroughDate: impound.paidThroughDate ?? null,
  lastPaymentAmount: toComparableAmount(impound.lastPaymentAmount),
});

const checkIsImpoundChanged = ({ impound, storedImpound }) =>
  !_.isNil(impound.monthsCollectedChange) ||
  !_.isEqual(getComparableImpound(impound), getComparableImpound(storedImpound));

export const getValuesToSend = ({
  applicationId,
  form,
  initialImpounds,
  impoundsTypes,
  impoundsTypesOptions,
  homeownersInsuranceImpoundId = null,
  isChattelLoan = false,
  isEscrowed = false,
  isEscrowApplicable = false,
}) => {
  const initialImpoundIds = new Set(_.map(initialImpounds, 'applicationImpoundId'));

  // Without the HOI conversion, so a row the form converted on load still counts as changed.
  const storedImpoundsById = _.keyBy(
    getInitialImpounds({
      impounds: initialImpounds,
      impoundsTypes,
      impoundsTypesOptions,
      homeownersInsuranceImpoundId,
      isImpoundConversionLocked: true,
    }).map(toRequestImpound),
    'applicationImpoundId',
  );

  const formattedFormImpounds = form.set_impounds.map(impound => ({
    ...toRequestImpound(impound),
    monthsCollectedChange: getMonthsCollectedChange({
      impound,
      isNew: !initialImpoundIds.has(impound.applicationImpoundId),
      isFollowable:
        isChattelLoan ||
        checkIsHomeownersInsuranceImpoundType(impound.impoundTypeId, homeownersInsuranceImpoundId),
      isChattelLoan,
      isEscrowed,
      isEscrowApplicable,
    }),
  }));

  const impoundsToDelete = formattedFormImpounds
    .filter(imp => imp.isDeleted)
    .map(imp => ({ applicationImpoundId: imp.applicationImpoundId }));
  const impoundsToCreate = _.differenceBy(
    formattedFormImpounds,
    initialImpounds,
    'applicationImpoundId',
  );
  const impoundsToUpdate = _.keyBy(
    formattedFormImpounds.filter(
      impound =>
        _.has(storedImpoundsById, impound.applicationImpoundId) &&
        !impound.isDeleted &&
        impound.pendingChangesActionType !== PendingChangesActionType.DELETED &&
        checkIsImpoundChanged({
          impound,
          storedImpound: storedImpoundsById[impound.applicationImpoundId],
        }),
    ),
    'applicationImpoundId',
  );

  return {
    impounds: {
      DELETE: impoundsToDelete,
      CREATE: impoundsToCreate,
      UPDATE: impoundsToUpdate,
    },
    monthsCushion: form.monthsCushion?.value,

    applicationId,
    requestMetadata: { tabId: LoanApplicationTabId.LOAN_FEES },
  };
};

export const getDueDateDuplicates = ({ formImpounds }) => {
  const dueDateDuplicates = formImpounds.reduce((acc, { impoundTypeName, amountDueDates }) => {
    const formattedDates = amountDueDates.map(group => ({
      ...group,
      dueDate: dayjs(group.dueDate).format(COMMON_REQUEST_DATE_FORMAT),
    }));
    const isDueDateDuplicated =
      formattedDates.length !== _.uniqBy(formattedDates, 'dueDate').length;

    if (isDueDateDuplicated) {
      acc.push(impoundTypeName);
    }

    return acc;
  }, []);

  return dueDateDuplicates;
};

export const isFieldValueChanged = ({ value, initialValue, modifiedFields, prop }) => {
  const isModifiedValue = _.includes(modifiedFields, prop);
  const isTempValueChanged = !!value && String(value) !== String(initialValue);

  return isModifiedValue || isTempValueChanged;
};

export const getIsNotDeleted = i => i.pendingChangesActionType !== PendingChangesActionType.DELETED;
